import { hasAnalyticsConsent, trackLeadSubmit } from "@/lib/analytics";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
  "ref",
] as const;

function collectUtm(): Record<string, string> {
  const utm: Record<string, string> = {};
  if (typeof window === "undefined") return utm;
  const params = new URLSearchParams(window.location.search);
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm[key] = value.slice(0, 200);
  }
  return utm;
}

export type LeadPayload = Record<string, unknown> & { request_type: string; name: string };

/**
 * Sends a lead to the server-side edge function.
 * Resolves only when the request was actually stored server-side.
 *
 * Conversion signal: the server answers a filled honeypot with a fake
 * `{success, id}` (no `notified` field) and a repeat with `duplicate: true`.
 * Only a newly stored lead (has `notified`, not duplicate, empty honeypot)
 * emits `lead_submit`.
 */
export async function submitLead(
  payload: LeadPayload,
  fetchImpl: typeof fetch = fetch,
): Promise<{ id: string; notified: boolean }> {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const res = await fetchImpl(
    `https://${projectId}.supabase.co/functions/v1/submit-contact-request`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        source_page: typeof window !== "undefined" ? window.location.pathname : null,
        utm: collectUtm(),
        // Explicit boolean: current v2 consent covers advertising measurement.
        // The server must send its OpenAI conversion only for strict `true`.
        ad_conversion_consent: hasAnalyticsConsent() === true,
      }),
    },
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || "Failed to submit request");
  }

  const honeypot = typeof payload.website === "string" && payload.website.trim() !== "";
  const newlyStored = typeof data.notified === "boolean" && data.duplicate !== true;
  if (!honeypot && newlyStored) trackLeadSubmit(String(payload.request_type));

  return { id: data.id as string, notified: Boolean(data.notified) };
}
